// =============================================================================
// SIREPPAR - Consultas a la SEP: bandeja del partido (#388)
// =============================================================================
// Todas las consultas de la agrupación de la sesión (para una alianza, la alianza
// activa). Las ven todos los usuarios habilitados del partido.
// =============================================================================

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { PlusIcon } from '@heroicons/react/24/outline';
import DataTable, { Column } from '../../components/common/DataTable';
import EstadoConsultaBadge from '../../components/common/EstadoConsultaBadge';
import { consultaService } from '../../services/api';
import { usePuedeCrearConsulta } from '../../hooks/usePuedeCrearConsulta';
import { ETIQUETA_ESTADO_CONSULTA, ETIQUETA_ORIGEN, EstadoConsulta, OrigenMensaje, formatearFecha } from '../../utils/consultas';

interface FilaConsulta {
  id: string;
  asunto: string;
  estado: EstadoConsulta;
  ultimoMensajeEn: string;
  origenUltimoMensaje: OrigenMensaje | null;
  expediente: { id: string; numero: string } | null;
  /** Consulta que inició la SEP y el partido todavía no contestó. */
  sinMensajesDelPartido?: boolean;
  /** Hay algo de la SEP que el partido todavía no vio. */
  nuevoParaPartido?: boolean;
}

export default function ConsultasPage() {
  const navigate = useNavigate();
  const { puedeCrear } = usePuedeCrearConsulta();
  const [estado, setEstado] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const params: Record<string, string> = { page: String(page), pageSize: String(pageSize), ...(estado ? { estado } : {}) };

  const { data, isLoading } = useQuery({
    queryKey: ['consultas', params],
    queryFn: () => consultaService.list(params),
  });

  const filas: FilaConsulta[] = data?.data ?? [];
  const total: number = data?.meta?.totalItems ?? 0;

  const columns: Column<FilaConsulta>[] = [
    {
      key: 'asunto',
      header: 'Asunto',
      render: (valor, fila) => (
        <span className="font-medium text-gray-900">
          {valor}
          {fila.nuevoParaPartido && (
            <span className="ml-2 inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
              Nuevo
            </span>
          )}
        </span>
      ),
    },
    { key: 'expediente', header: 'Expediente', render: (_v, fila) => fila.expediente?.numero ?? '—' },
    { key: 'estado', header: 'Estado', render: (_v, fila) => <EstadoConsultaBadge estado={fila.estado} recibida={fila.sinMensajesDelPartido} /> },
    { key: 'ultimoMensajeEn', header: 'Último mensaje', render: (_v, fila) => formatearFecha(fila.ultimoMensajeEn) },
    {
      key: 'origenUltimoMensaje',
      header: 'Último mensaje de',
      render: (_v, fila) => (fila.origenUltimoMensaje ? ETIQUETA_ORIGEN[fila.origenUltimoMensaje] : '—'),
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Consultas a la SEP</h1>
          <p className="text-sm text-gray-500 mt-1">Consultas de su agrupación a la Secretaría Electoral Permanente</p>
        </div>
        {puedeCrear && (
          <button
            onClick={() => navigate('/consultas/nueva')}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
          >
            <PlusIcon className="w-4 h-4" />
            Nueva consulta
          </button>
        )}
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4 flex flex-wrap gap-3">
        <label className="text-sm text-gray-700 flex items-center gap-2">
          Estado
          <select
            value={estado}
            onChange={(e) => { setEstado(e.target.value); setPage(1); }}
            className="border border-gray-300 rounded-md px-3 py-1.5 text-sm"
          >
            <option value="">Todos</option>
            {(Object.keys(ETIQUETA_ESTADO_CONSULTA) as EstadoConsulta[]).map((e) => (
              <option key={e} value={e}>{ETIQUETA_ESTADO_CONSULTA[e]}</option>
            ))}
          </select>
        </label>
      </div>

      <DataTable
        data={filas}
        columns={columns}
        loading={isLoading}
        emptyMessage={puedeCrear ? 'Todavía no hay consultas. Use «Nueva consulta» para enviar la primera.' : 'Todavía no hay consultas.'}
        onRowClick={(fila) => navigate(`/consultas/${fila.id}`)}
        pagination={{
          page,
          pageSize,
          total,
          onPageChange: setPage,
          onPageSizeChange: (n) => { setPageSize(n); setPage(1); },
        }}
      />
    </div>
  );
}
